/**
 * Generates a color from a predefined palette based on a string.
 * Safe for null/undefined input values.
 * 
 * @param {string} str - The string to generate a color from
 * @returns {string} A hex color code from the predefined palette
 */
export const getColorFromString = (str) => {
  const colors = ['#7209b7', '#f72585', '#4361ee', '#4cc9f0', '#560bad', '#3a0ca3'];
  
  // Handle null, undefined, or empty string
  if (!str) {
    return colors[0]; // Return first color as default
  }
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
};

/**
 * Generates a deterministic dark color based on a string input.
 * The color will be consistent for the same input string,
 * but different input strings will produce different colors.
 * 
 * @param {string} str - Input string to generate color from
 * @param {object} options - Optional parameters
 * @param {number} options.saturation - Color saturation (0-100), default 80
 * @param {number} options.lightness - Color lightness (0-100), default 25-45 (variable)
 * @returns {string} CSS hex color code (e.g., #123456)
 */
export const generateDarkColor = (str, options = {}) => {
  if (!str || typeof str !== 'string') {
    return '#333333'; // Default fallback color
  }

  // We'll use the first half of the string to determine hue and the second half for lightness
  // This increases variance among similar strings
  const firstHalf = str.substring(0, Math.ceil(str.length / 2));
  const secondHalf = str.substring(Math.ceil(str.length / 2));

  // Default options
  const saturation = options.saturation || 80; // Higher saturation for more vibrant colors

  // Generate a hash from the string
  let hash = 0;
  for (let i = 0; i < firstHalf.length; i++) {
    // Improved hash function with more variance
    hash = ((hash << 5) - hash) + firstHalf.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Generate a secondary hash for lightness variation
  let secondaryHash = 0;
  for (let i = 0; i < secondHalf.length; i++) {
    secondaryHash = ((secondaryHash << 5) - secondaryHash) + secondHalf.charCodeAt(i);
    secondaryHash = secondaryHash & secondaryHash;
  }

  // We want to ensure different strings get colors that are visually distinct
  // We'll use a pre-defined list of hue values that are well-spaced across the spectrum
  const colorHueSpaces = [
    0,    // Red
    30,   // Orange-red
    60,   // Yellow
    100,  // Yellow-green
    140,  // Green
    180,  // Cyan
    210,  // Sky blue
    240,  // Blue
    270,  // Purple
    300,  // Magenta
    330   // Pink
  ];
  
  // Select a base hue from our well-spaced hues
  const baseHueIndex = Math.abs(hash) % colorHueSpaces.length;
  const baseHue = colorHueSpaces[baseHueIndex];
  
  // Add some variance within that hue range (±15 degrees)
  // This gives us more unique colors while still maintaining good separation
  const hueVariance = (Math.abs(hash) % 31) - 15; // Range: -15 to +15
  const hue = (baseHue + hueVariance + 360) % 360;
  
  // Vary the lightness for more diversity but keep it dark enough for white text
  // Range between 25% and 40% - dark enough for white text but with some variation
  const lightnessMin = options.lightnessMin || 25;
  const lightnessMax = options.lightnessMax || 40;
  const lightnessRange = lightnessMax - lightnessMin;
  const lightness = lightnessMin + (Math.abs(secondaryHash) % lightnessRange);
  
  // Convert HSL to RGB
  const h = hue / 360;
  const s = saturation / 100;
  const l = lightness / 100;
  
  const hueToRgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgb(p, q, h + 1/3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1/3);
  }
  
  // Convert RGB values to hex
  const toHex = (x) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  const hexColor = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  return hexColor;
};

export default {
  generateDarkColor,
  getColorFromString
};